/**
 * 訂單詳情頁面
 */

import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { 
  ArrowLeft, Package, MapPin, User, Mail, Phone,
  Calendar, CreditCard, CheckCircle, Clock, XCircle
} from 'lucide-react';

export function OrderDetailPage() {
  const { orderNumber } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchOrderDetail();
  }, [orderNumber]);

  const fetchOrderDetail = async () => {
    try {
      const { data, error } = await supabase
        .from('orders')
        .select('*')
        .eq('order_number', orderNumber)
        .single();

      if (error) throw error;
      setOrder(data);
    } catch (error) {
      console.error('查詢訂單詳情失敗:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-800',
      paid: 'bg-green-100 text-green-800',
      failed: 'bg-red-100 text-red-800',
      confirmed: 'bg-blue-100 text-blue-800',
      processing: 'bg-purple-100 text-purple-800',
      shipped: 'bg-indigo-100 text-indigo-800',
      delivered: 'bg-emerald-100 text-emerald-800',
      cancelled: 'bg-gray-100 text-gray-800',
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'paid':
      case 'confirmed':
      case 'delivered':
        return <CheckCircle className="h-5 w-5" />;
      case 'pending':
      case 'processing':
        return <Clock className="h-5 w-5" />;
      case 'failed':
      case 'cancelled':
        return <XCircle className="h-5 w-5" />;
      default:
        return <Package className="h-5 w-5" />;
    }
  };

  const getStatusText = (status) => {
    const statusMap = {
      pending: '待付款',
      paid: '已付款',
      failed: '付款失敗',
      confirmed: '已確認',
      processing: '處理中',
      shipped: '已出貨',
      delivered: '已送達',
      cancelled: '已取消',
    };
    return statusMap[status] || status;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-cyan-50 to-teal-50 py-8">
        <div className="container mx-auto px-4 max-w-4xl">
          <Card>
            <CardContent className="py-12 text-center">
              <Package className="h-16 w-16 text-gray-300 mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-gray-900 mb-2">訂單不存在</h2>
              <p className="text-gray-600 mb-6">找不到訂單編號 {orderNumber}</p>
              <Button onClick={() => navigate('/member')}>返回會員中心</Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-cyan-50 to-teal-50 py-8">
      <div className="container mx-auto px-4 max-w-4xl">
        {/* 返回按鈕 */}
        <Button
          variant="ghost"
          onClick={() => navigate('/member')}
          className="mb-6"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          返回會員中心
        </Button>

        {/* 訂單狀態卡片 */}
        <Card className="mb-6 bg-white/80 backdrop-blur-xl border-white/20 shadow-lg">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-2xl mb-2">訂單詳情</CardTitle>
                <CardDescription className="text-lg font-mono">
                  {order.order_number}
                </CardDescription>
              </div>
              <Badge className={`${getStatusColor(order.payment_status)} text-lg px-4 py-2`}>
                <div className="flex items-center gap-2">
                  {getStatusIcon(order.payment_status)}
                  {getStatusText(order.payment_status)}
                </div>
              </Badge>
            </div>
          </CardHeader>

          <CardContent className="space-y-6">
            {/* 訂單時間 */}
            <div className="flex items-center gap-3 text-gray-600">
              <Calendar className="h-5 w-5" />
              <span>下單時間: {new Date(order.created_at).toLocaleString('zh-TW')}</span>
            </div>

            <Separator />

            {/* 產品資訊 */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">產品資訊</h3>
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex justify-between items-start mb-2">
                  <div>
                    <p className="font-medium text-gray-900">{order.product_name}</p>
                    <p className="text-sm text-gray-500">單價: NT$ {order.unit_price.toLocaleString()}</p>
                  </div>
                  <p className="text-sm text-gray-600">x {order.quantity}</p>
                </div>
                {order.discount_code && (
                  <div className="mt-2 text-sm text-green-600">
                    使用折扣碼: {order.discount_code}
                  </div>
                )}
                <div className="mt-4 pt-4 border-t flex justify-between items-center">
                  <span className="font-semibold text-gray-900">訂單總額</span>
                  <span className="text-2xl font-bold text-blue-600">
                    NT$ {order.total_amount.toLocaleString()}
                  </span>
                </div>
              </div>
            </div>

            <Separator />

            {/* 收件人資訊 */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">收件人資訊</h3>
              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <User className="h-5 w-5 text-gray-400 mt-0.5" />
                  <div>
                    <p className="text-sm text-gray-500">姓名</p>
                    <p className="font-medium text-gray-900">{order.recipient_name}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Phone className="h-5 w-5 text-gray-400 mt-0.5" />
                  <div>
                    <p className="text-sm text-gray-500">電話</p>
                    <p className="font-medium text-gray-900">{order.recipient_phone}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Mail className="h-5 w-5 text-gray-400 mt-0.5" />
                  <div>
                    <p className="text-sm text-gray-500">Email</p>
                    <p className="font-medium text-gray-900 break-all">{order.recipient_email}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <MapPin className="h-5 w-5 text-gray-400 mt-0.5" />
                  <div>
                    <p className="text-sm text-gray-500">地址</p>
                    <p className="font-medium text-gray-900">{order.recipient_address}</p>
                  </div>
                </div>
              </div>
            </div>

            <Separator />

            {/* 付款資訊 */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">付款資訊</h3>
              <div className="space-y-2">
                <div className="flex items-center gap-3">
                  <CreditCard className="h-5 w-5 text-gray-400" />
                  <span className="text-gray-600">
                    {order.payment_method === 'CREDIT' ? '信用卡' : order.payment_method}
                  </span>
                </div>
                {order.transaction_id && (
                  <div className="text-sm text-gray-500">
                    交易編號: {order.transaction_id}
                  </div>
                )}
              </div>
            </div>

            {/* 備註 */}
            {order.notes && (
              <>
                <Separator />
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">訂單備註</h3>
                  <p className="text-gray-600">{order.notes}</p>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* 操作按鈕 */}
        <div className="flex gap-4">
          <Button
            variant="outline"
            onClick={() => navigate('/member')}
            className="flex-1"
          >
            返回會員中心
          </Button>
          {order.payment_status === 'paid' && (
            <Button
              onClick={() => navigate('/product')}
              className="flex-1 bg-gradient-to-r from-blue-600 to-cyan-600"
            >
              再次購買
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

export default OrderDetailPage;
